import Vue from 'vue';
import WidgetMenu from 'components/DataWidgets/WidgetMenu/WidgetMenu.vue';
import WidgetFrame from 'src/components/widgets/WidgetFrame/WidgetFrame.vue';
import icon from 'assets/img/dashboards/dash-sentiment.svg';
import errorIcon from 'assets/icons/alert-bubble.svg';
import DownloadExportButton from 'components/project/analysis/results/widgets/DownloadExportButton.vue';
import Timeline from 'src/components/project/analysis/results/widgets/Timeline.vue';
import { getCsvData, regroupData } from './SentimentTimeline.utils';
import { getAggregationOffset, getDataAbsMax, makeTimelineSlide } from '../DataWidgetUtils';
import StatsRow from './StatsRow.vue';
import WidgetMessagePanel from 'components/widgets/WidgetMessagePanel/WidgetMessagePanel.vue';
export default Vue.extend({
    components: {
        WidgetFrame,
        DownloadExportButton,
        Timeline,
        WidgetMenu,
        StatsRow,
        WidgetMessagePanel,
    },
    props: {
        /** data to render */
        data: { type: Object, required: false, default: null },
        isZoomed: { type: Boolean, required: false, default: false },
        zoomToRoute: { type: Object, required: false, default: null },
        overallData: { type: Object, required: false, default: null },
        group: { type: String, required: false, default: 'overall__' },
        exportName: { type: String, required: false, default: '' },
        /** `fetching`, `done` or `''` */
        status: { type: String, required: false, default: '' },
        /** error object for dev panel */
        error: { type: [Error, Object], required: false, default: null },
        /** nicer error message for user  */
        userError: { type: String, required: false, default: null },
        startTime: { type: Number, required: false, default: null },
        doneTime: { type: Number, required: false, default: null },
        devMode: { type: Boolean, required: false, default: false },
        /** widget banner to display */
        banner: { type: Object, default: () => null, required: false },
        dateFields: { type: Array, required: true },
        defaultDateField: { type: String, required: false, default: null },
        weekStart: { type: String, required: false, default: null },
        /** Add a skeleton mask (used when reloading state between dashboards) */
        masked: { type: Boolean, required: false, default: false },
        config: { type: Object, required: false, default: null },
        dayFirstDates: { type: Boolean, required: false, default: false },
    },
    data() {
        return {
            icon,
            errorIcon,
            widgetErrors: false,
            resolution: 'monthly',
            yRange: [0, 1],
            yLabel: 'Records',
            selectedDataInPlottableFormat: [],
            records: {},
            // Default menu options
            menuSelections: {
                'Date Field': null,
                'Display': 'Values',
                'Resolution': 'Monthly',
            },
        };
    },
    computed: {
        hasDate() {
            return this.dateFields.length > 0;
        },
        // If the timeline is being shown on the dashboard overview, then the data
        // is the overall data. This means we cannot calculate impact on X because
        // there is no subset of data to use in the calculation. (e.g if we were
        // showing the stats for a theme, the impact would be calculated using
        // the impact of the theme ON the overall dataset).
        hasImpact() {
            return this.group !== 'overall__';
        },
        displayOptions() {
            return {
                'label': 'Frequency (%)',
                'numberType': 'percentage',
                'yAxisLabel': this.showingImpact ? 'Impact' : 'Values',
                'yCapValue': 2,
                'yMultipleOf': 0.1,
            };
        },
        menus() {
            const options = [{
                    name: 'Date Field',
                    selection: this.menuSelections['Date Field'],
                    options: [
                        [{
                                title: 'Date Field',
                                type: 'menu',
                                showSelected: true,
                                selected: this.menuSelections['Date Field'],
                                options: this.dateFields.map(({ name }) => name)
                            }]
                    ],
                }, {
                    name: 'Resolution',
                    selection: this.menuSelections['Resolution'],
                    options: [
                        [{
                                title: 'Resolution',
                                type: 'menu',
                                showSelected: true,
                                selected: this.menuSelections['Resolution'],
                                options: ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Yearly']
                            }]
                    ],
                }];
            if (this.hasImpact) {
                // We want the display menu to show up between date field and resolution
                options.splice(1, 0, {
                    name: 'Display',
                    selection: this.menuSelections['Display'],
                    options: [
                        [{
                                title: 'Display',
                                type: 'menu',
                                showSelected: true,
                                selected: this.menuSelections['Display'],
                                options: ['Values', 'Impact']
                            }]
                    ],
                });
            }
            return options;
        },
        querySentiment() {
            const groupRow = this.overallData?.data?.payload?.find((p) => p.group__ == this.group);
            const format = (value) => {
                return (value ?? 0).toFixed(0);
            };
            return this.showingImpact ? {
                positive: format(groupRow?.['sentiment__|positive%i_rto__']),
                negative: format(groupRow?.['sentiment__|negative%i_rto__']),
                mixed: format(groupRow?.['sentiment__|mixed%i_rto__']),
                neutral: format(groupRow?.['sentiment__|neutral%i_rto__']),
            } : {
                positive: format(groupRow?.['sentiment__|positive%__']),
                negative: format(groupRow?.['sentiment__|negative%__']),
                mixed: format(groupRow?.['sentiment__|mixed%__']),
                neutral: format(groupRow?.['sentiment__|neutral%__']),
            };
        },
        showingImpact() {
            return this.menuSelections['Display'] === 'Impact';
        }
    },
    watch: {
        config: {
            deep: true,
            handler() {
                this.setOptionsFromConfig();
            }
        },
        menuSelections: {
            deep: true,
            handler() {
                this.fetchData();
            }
        },
        'menuSelections.Display'() {
            this.regroupData();
        },
        data(val) {
            if (val === null)
                return;
            this.regroupData();
        },
        group() {
            this.fetchData();
        },
    },
    mounted() {
        if (this.hasDate) {
            this.menuSelections['Date Field'] = this.defaultDateField ?? this.dateFields[0].name;
        }
        this.setOptionsFromConfig();
        if (this.data === null) {
            this.fetchData();
        }
        else {
            this.regroupData();
        }
    },
    methods: {
        updateConfig() {
            const options = {
                resolution: this.menuSelections['Resolution'],
                dateField: this.menuSelections['Date Field'],
                display: this.menuSelections['Display'],
            };
            const updated = Object.assign({}, this.config, { options });
            this.$emit('config-changed', updated);
        },
        setOptionsFromConfig() {
            const resolution = this.config?.options?.resolution ?? 'Monthly';
            const dateField = this.config?.options?.dateField ?? (this.defaultDateField ?? (this.hasDate && this.dateFields[0].name));
            const display = this.config?.options?.display ?? 'Values';
            this.setMenuSelection(null, ['Resolution', resolution], true);
            this.setMenuSelection(null, ['Date Field', dateField], true);
            this.setMenuSelection(null, ['Display', display], true);
        },
        setMenuSelection(_, path, fromConfig = false) {
            this.menuSelections[path[0]] = path[1];
            if (!fromConfig)
                this.updateConfig();
        },
        regroupData() {
            if (!this.hasDate || !this.data)
                return;
            const [records, ...groupedData] = regroupData(this.data, this.menuSelections['Date Field'], this.menuSelections['Display'] === 'Impact', this.group);
            // only show series with data
            this.selectedDataInPlottableFormat = groupedData.filter(({ counts = [] }) => counts.length > 0);
            this.records = records;
            this.calculateYRange();
        },
        fetchData(force = false) {
            const blocks = [{
                    'aggfuncs': [{
                            'new_column': 'frequency',
                            'src_column': 'document_id',
                            'aggfunc': 'count'
                        }],
                    'pivot_field': 'sentiment__',
                    'metric_calculator': 'sentiment'
                }];
            const requirements = {
                blocks,
                date_fieldname: this.menuSelections['Date Field'],
                date_aggregation_offset: getAggregationOffset(this.menuSelections['Resolution']),
                week_start: this.weekStart
            };
            if (this.hasDate) {
                // Data by date resolution
                this.$emit('requires', 'sentiment-timeline', requirements, force, true);
            }
            // Overall data
            this.$emit('requires', 'sentiment-timeline-overall', {
                blocks: [{
                        "aggfuncs": [{
                                "new_column": "frequency",
                                "src_column": "document_id",
                                "aggfunc": "count"
                            }],
                        "pivot_field": "sentiment__",
                        "metric_calculator": "sentiment"
                    }]
            }, force, true);
        },
        refresh() {
            window.location.reload();
        },
        contact() {
            try {
                window.Intercom('show');
            }
            catch {
                console.warn('intercom show failed');
            }
        },
        reload() {
            this.fetchData(true);
        },
        getCsvData() {
            return getCsvData(this.selectedDataInPlottableFormat, this.menuSelections);
        },
        calculateYRange() {
            const visibleDataset = this.selectedDataInPlottableFormat.filter(series => series.visible);
            const dataMax = getDataAbsMax(visibleDataset, this.displayOptions.yCapValue, this.displayOptions.yMultipleOf);
            const dataMin = this.showingImpact ? -dataMax : 0;
            this.yRange = [dataMin, dataMax];
        },
        getExportConfig() {
            return {
                dims: this.getTrendEl().getBoundingClientRect(),
                css: `
          text {
            color: #383838;
            font-size: 14px;
            stroke: none;
          }
          .line {
            stroke-width: 2px;
          }
          .axis path, .axis line {
            shape-rendering: crispEdges;
            stroke: #ebebeb;
            stroke-width: 2px;
            opacity: 0.5;
          }
        `
            };
        },
        getTrendEl() {
            return this.$el.querySelector('div.content');
        },
        makePptSlide(pptx) {
            const slide = pptx.addSlide();
            makeTimelineSlide(pptx, slide, this.selectedDataInPlottableFormat.map((d) => ({
                ...d,
                // Format the counts as percentages
                counts: d.counts.map((c) => c * 100),
            })), this.exportName + ' - Sentiment Timeline', 'Percentage', this.dayFirstDates);
        },
    },
});
